import { User } from '@upright/common/iam/users/types';
import {
  allProductSetStatuses,
  companyDataPointKeys,
  productSetCreationKeys,
} from '@upright/common/model/input/blanket/blanket-product-set/index';
import { PopulatedBlanketProduct } from '@upright/common/model/input/blanket/blanket-product/types';
import { CompanyProductSet } from '../../companies/products/types';
import { Comment } from '@upright/common/internal/comments/types';
import { GPTProductSetGenerationTrigger } from '@upright/common/internal/jobs/types';

import { Op } from 'sequelize';

export interface BlanketProductSet {
  id: number;

  // companyId and ownerUserId can't coexist on the same BlanketProductSet, even though the typings don't force that.
  // As of 11/2022, the assertion of correct combinations is done in assertValidBlanketProductSetCreationParams
  companyId?: string;
  companyName?: string | null;

  ownerUserId?: number | null;
  ownerUser?: BlanketOwnerUser;

  source: string;
  sourceUrl: string;
  year: number | null;
  status: BlanketProductSetStatus;

  isCompanyPlatformProductSet: boolean;
  isUserProductSet: boolean;

  employeeCount: number | null;
  employeeCountYear: number | null;
  revenue: number | null;
  revenueCurrency: string | null;
  revenueYear: number | null;

  createdAt: string;
  createdBy: number;
  createdByUser?: BlanketTimestampUser;

  updatedAt: string;
  updatedBy: number;
  updatedByUser?: BlanketTimestampUser;

  productsEditedAt?: string;
  productsEditedByUser?: BlanketTimestampUser;

  publishedAt: string | null;
  publishedBy: number | null;
  publishedByUser?: BlanketTimestampUser;

  ownerOrganizationId?: number;
  ownerOrganizationName?: string;

  // PLT-4471 Phase 3: target user-group for the eventual published company product set's
  // `readableBy`. Collected at create time, confirmable at publish time. Null / undefined
  // mean "no explicit choice yet — UI defaults to EVERYONE in the picker". This is purely a
  // draft-time plan; the actual access write happens on the resulting `company_product_sets`
  // row. Optional in the shared type so existing callers (and the create-blanket payload)
  // don't have to thread it through unless they're using the new picker.
  plannedReadableBy?: number | null;

  sourceMetadata?: BlanketSourceMetadata;

  // The linked company product set for published blanket product sets
  // Should only have one active linked company product set, but can have many archived product sets.
  // The archived ones are filtered out before composing this object
  companyProductSet?: CompanyProductSet;
  documentPageNumber: string;
  documentUrl?: string; // Note: documentUrl is stored in sourceUrl for old blankets

  comments?: Comment[];

  enableShareLink: boolean;

  /**
   * Authoritative set-level IPRA flag (PLT-5539). Seeded once from the legacy
   * source-based derivation, then the single source of truth. Do not re-derive
   * IPRA-ness from `sourceMetadata.sourceId` — read this instead.
   */
  isIpra: boolean;
}

export type CreatedAtDateQueryParams =
  | { [Op.lt]: Date }
  | { [Op.gt]: Date }
  | { [Op.between]: [Date, Date] }
  | undefined;

export type ListAllQueryParams = {
  limitQuery?: { limit?: number };
  createdAtQuery?: { createdAt?: CreatedAtDateQueryParams };
};

export type PopulatedBlanketProductSet = BlanketProductSet & {
  products?: PopulatedBlanketProduct[];
};
export type BlanketProductSetStatus = (typeof allProductSetStatuses)[number];
export type UpdateBlanketProductSetStatusParams = Pick<BlanketProductSet, 'status'>;
export type ProductSetCreationKeys = (typeof productSetCreationKeys)[number];
export type CreateBlanketProductSetParams = Pick<BlanketProductSet, ProductSetCreationKeys> &
  Partial<Pick<BlanketProductSet, 'sourceMetadata'>> &
  // `isIpra` is create-only: kept out of `productSetCreationKeys` (which also gates the update
  // path, parked in PLT-5571) and threaded in as an optional field, mirroring `sourceMetadata`.
  Partial<Pick<BlanketProductSet, 'isIpra'>> &
  Partial<Pick<BlanketProductSet, CompanyDataPointKeys>>;

export type CompanyDataPointKeys = (typeof companyDataPointKeys)[number];
export type BlanketCompanyDataPoints = Partial<Pick<BlanketProductSet, CompanyDataPointKeys>>;

// Convention: for every sourceId, describe where it is coming from.
export enum BlanketSourceId {
  // produced by https://github.com/uprightproject/xbrl-parser
  SEC_EDGAR_XBRL = 'SEC_EDGAR_XBRL',
  // produced by https://github.com/uprightproject/xbrl-parser
  SEC_EDGAR_AND_LEGACY = 'SEC_EDGAR_AND_LEGACY',
  // i.e., produced by https://github.com/uprightproject/upright-report-collector
  ANNUAL_REPORT_COLLECTOR = 'ANNUAL_REPORT_COLLECTOR',
  // duplicated by a user of the platform
  DUPLICATED_INTERNALLY = 'DUPLICATED_INTERNALLY',
  // Produced by pressing the Pair button
  MANUALLY_TRIGGERED_PAIRING = 'MANUALLY_TRIGGERED_PAIRING',
  // Produced by GPT without the use of external inputs, e.g. GPT was queried by what does it think the company produces,
  // instead of passing an HTML page to extract relevant data.
  GPT_AS_KNOWLEDGEBASE = 'GPT_AS_KNOWLEDGEBASE',
  // Produced by the AutoGPT microservice, a web browser browses websites and then GPT summarizes it.
  AUTOGPT = 'AUTOGPT',
  // Produced by the Company Mapping microservice, utilizing LLM web search and other techniques.
  COMPANY_MAPPING_SERVICE = 'COMPANY_MAPPING_SERVICE',
  // Produced by Segment split GPT job from Annual report
  SEGMENT_GPT = 'SEGMENT_GPT',
  // Produced by both AutoGPT microserviceand  Segment split GPT  from Annual report
  SEGMENT_AUTOGPT = 'SEGMENT_AUTOGPT',
  UPCLOSE_SEGMENT_SPLIT_EXTRACTION = 'UPCLOSE_SEGMENT_SPLIT_EXTRACTION',
  // New product input UI that used the intermediate product representation
  PRODUCT_INPUT_UI = 'PRODUCT_INPUT_UI',
  // Produced by the APG+ revenue-segments pipeline (company-mapping
  // /revenue-segments): extract segments from a financial report, then
  // discover + taxonomy-map products per segment.
  REVENUE_SEGMENTS = 'REVENUE_SEGMENTS',
}

export interface BlanketXBRLParserSourceTrace {
  sourceId: BlanketSourceId.SEC_EDGAR_XBRL;
  accessionId: string;
  sourceYear: number;
  parserVersion: string;
}

export interface BlanketManuallyTriggeredPairingTrace {
  sourceId: BlanketSourceId.MANUALLY_TRIGGERED_PAIRING;
  sourceProductSetId: number;
  userId: number;
  existingMetadata: BlanketSourceMetadata | null;
}

interface BlanketMergedProductSetTrace {
  sourceId: BlanketSourceId.SEC_EDGAR_AND_LEGACY;
  externalSource: BlanketXBRLParserSourceTrace;
  internalSource: { productSetId: number };
  version: string;
}

interface BlanketDuplicatedInternallySourceTrace {
  sourceId: BlanketSourceId.DUPLICATED_INTERNALLY;
  duplicatedFrom: number;
  originalMetadata?: BlanketSourceMetadata;
}

interface BlanketReportCollectorTrace {
  sourceId: BlanketSourceId.ANNUAL_REPORT_COLLECTOR;
  // Expect e.g., automated page detection or business segment detection in the future
  scope: ReportCollectorScope.SOURCE_REPORT_ONLY;
  version: string;
  // Expect source URL stored in IBlanketProductSet.sourceUrl
}

interface GPTAsKnowledgeBaseSourceTrace {
  sourceId: BlanketSourceId.GPT_AS_KNOWLEDGEBASE;
  gptVersion: string;
  semanticPairingVersion: string;
}

export interface AutoGPTSourceTrace {
  sourceId: BlanketSourceId.AUTOGPT;
  gptVersion: string;
  semanticPairingVersion: string;
  backendVersion: string;
}

export interface CompanyMappingServiceSourceTrace {
  sourceId: BlanketSourceId.COMPANY_MAPPING_SERVICE;
  trigger?: GPTProductSetGenerationTrigger;
  /**
   * PLT-7638: the generated set this one copies, when generation reused a recent set rather than
   * running (`duplicateGptProductSet`). Absent on sets the service generated, and on copies made
   * before it was recorded.
   */
  copiedFromProductSetId?: number;
}

export interface SegmentGPTSourceTrace {
  sourceId: BlanketSourceId.SEGMENT_GPT;
  segmentSearchVersion: string;
  segmentPassageRanks: number[] | undefined;
  segmentCompletionIds: number[] | undefined;
  segmentRankError: string | undefined;
  segmentParseError: string | undefined;
  segmentYears: number[] | undefined;
  backendVersion: string;
}

export interface SegmentAutoGPTSourceTrace
  extends Omit<AutoGPTSourceTrace, 'sourceId'>, Omit<SegmentGPTSourceTrace, 'sourceId'> {
  sourceId: BlanketSourceId.SEGMENT_AUTOGPT;
}

export interface UpcloseSegmentSplitExtractionTrace {
  sourceId: BlanketSourceId.UPCLOSE_SEGMENT_SPLIT_EXTRACTION;
  upcloseReportId: string;
}

export interface ProductInputUISourceTrace {
  sourceId: BlanketSourceId.PRODUCT_INPUT_UI;
  /**
   * PLT-7638: `'chat'` when the chat's tools saved the set's current content. Absent when the product
   * editor did, and on sets saved before it was recorded. A draft is updated in place by every save,
   * so this describes the last one.
   */
  savedVia?: 'chat';
}

/**
 * Product-set-level provenance for an APG+ (revenue-segments) draft. Holds the
 * parts of the `/revenue-segments` response that don't map onto the blanket
 * schema — the extraction/verification metadata and the source-document
 * coordinates — so the draft is auditable without re-running the pipeline.
 */
export interface RevenueSegmentsSourceTrace {
  sourceId: BlanketSourceId.REVENUE_SEGMENTS;
  mode: 'upclose' | 'od' | 'url';
  /** upclose collected-report id, when sourced from an upclose annual report. */
  upcloseReportId?: number;
  /** url mode: the web PDF link the document was downloaded from. */
  sourcePdfUrl?: string;
  verified?: boolean | null;
  verifyNote?: string | null;
  retries?: number;
  costUsd?: number;
  segmentType?: string;
  breakdownSource?: string | null;
  sourceFile?: string;
  sourcePage?: string | null;
  unit?: string;
  extractionStatus?: string;
  reasoning?: string;
}

export enum ReportCollectorScope {
  SOURCE_REPORT_ONLY = 'SOURCE_REPORT_ONLY',
}

type SourceInformationHasWellFormedSourceId<T extends { sourceId: BlanketSourceId }> =
  | { sourceId: BlanketSourceId }
  | T; // Type "safeguard" to make the union below shout if a type with improper `sourceId` is added.

// Keep in sync with src/services/main/upserve/modules/blanket/api-utils.ts
export type BlanketSourceMetadata = SourceInformationHasWellFormedSourceId<
  | BlanketXBRLParserSourceTrace
  | BlanketMergedProductSetTrace
  | BlanketReportCollectorTrace
  | BlanketDuplicatedInternallySourceTrace
  | BlanketManuallyTriggeredPairingTrace
  | GPTAsKnowledgeBaseSourceTrace
  | AutoGPTSourceTrace
  | SegmentGPTSourceTrace
  | SegmentAutoGPTSourceTrace
  | UpcloseSegmentSplitExtractionTrace
  | ProductInputUISourceTrace
  | RevenueSegmentsSourceTrace
>; // Add other types as a Union inside the chevrons

export interface ProductSetAccessToken {
  productSetId: number;
  signature: string;
}

export enum BlanketProductSetOwnershipState {
  UPRIGHT = 'Upright',
  FREE_USER = 'Free user',
  COMPANY = 'Company',
}

export type BlanketTimestampUser = Pick<User, 'id' | 'username' | 'email'>;
export type BlanketOwnerUser = Pick<User, 'id' | 'username' | 'email' | 'verified' | 'extraData' | 'groups'>;
